import { epley, isE1rmRepCount } from "./pr.js";
import { nextLoadableUp, type LoadableContext } from "./roundLoadable.js";

// =========================================================================
// EQ-2 fetta 1 — the panel stops handing over data and PROPOSES.
// =========================================================================
// "Oggi quando tappo sull'orologio per vedere lo storico di un esercizio vedo
//  quello che mi interessa solo se e' recente, altrimenti vedo lo storico di
//  un esercizio simile, e comunque ogni volta devo poi adattare."
//
// So this module turns ONE anchor observation into the concrete numbers the
// athlete would actually put on the bar. It does not decide WHICH anchor to
// use -- picking it (this exercise's last session, else the best-ranked
// similar exercise that has history) belongs to the history layer, which
// already ranks candidates. This is the arithmetic and nothing else.
//
// Decisions it encodes, all Antenore's, 2026-08-16
// (next/exercise-level-and-proposal.md §7):
//
//   §7.4  THREE options, not two: more reps at the same load, more load at
//         the same reps, and THE TRADE (heavier, fewer reps). Measured on
//         2343 of his real session-to-session transitions: the trade is 40%
//         of what he actually does, while the pure "+1 rep at the same load"
//         -- the example this whole document was born from -- is 6.2%. Two
//         options would describe one passage in five.
//   §7.3  Every proposal carries WHERE IT CAME FROM. With 137 of his 162
//         exercises having no level of their own, the fallback is the normal
//         case, so a proposal that hides its source is a proposal that hides
//         how much it is worth.
//   §7.2  ...and HOW OLD the anchor is. Never decayed, never silently
//         adjusted: the number stays what it was and the age is stated. One
//         resumption in six happens after a 30+ day gap (measured over 2342
//         resumptions), so the age is ordinary information, not an edge case.
//
// ⛔ NOT here: the interruption budget of §7.1 ("something happened"). That
// needs a dispersion to derive itself from, which needs the per-exercise
// level -- slice 2. This slice proposes; it never judges.

/** The single observation a proposal is anchored to. */
export type ProposalAnchor = {
  weightKg: number;
  reps: number;
  /** When the anchor set was performed, epoch ms. Drives `ageDays` (§7.2). */
  atMs: number;
};

/**
 * Where the anchor came from, so the panel can say it out loud (§7.3).
 *
 * `exercise` -- the athlete's own history on the very exercise being logged.
 * `similar`  -- a different exercise, ranked as the nearest thing they have
 *               actually done. `slug` is the foreign key, never a display
 *               name: the readable name is resolved through the catalog.
 */
export type ProposalSource = {
  kind: "exercise" | "similar";
  slug: string;
  /** How many sessions of that exercise stand behind the anchor. */
  sessionCount: number;
};

/** Which of the three axes a proposal moves along (§7.4). */
export type ProposalAxis = "reps" | "load" | "trade";

export type SetProposal = {
  axis: ProposalAxis;
  weightKg: number;
  reps: number;
  /**
   * Epley one-rep-equivalent of the proposed set. Named like `E1rmPoint`'s
   * `kgPerRep1` for the same reason: it is an estimate, not a weight anyone
   * has lifted, and the name is what keeps the two from being mixed up.
   */
  kgPerRep1: number;
  source: ProposalSource;
  /**
   * Whole days between the anchor set and now. ⛔ Never used to shrink or
   * decay the numbers -- §7.2 decided the level does not decay, it states its
   * age. A consumer renders it; it must not silently discount by it.
   */
  ageDays: number;
};

// The trade climbs two rungs rather than one: at one rung the reps rarely
// move, and a "trade" that proposes the same reps as the load option is not a
// third option at all -- it is a duplicate wearing a different label.
const TRADE_RUNGS = 2;

const daysBetween = (fromMs: number, toMs: number): number =>
  Math.max(0, Math.floor((toMs - fromMs) / 86_400_000));

/**
 * Build up to three proposals from one anchor.
 *
 * Returns [] when the anchor cannot support Epley arithmetic (no weight, reps
 * outside the 1..12 band the formula is reliable in). ⛔ Empty means "we have
 * nothing to say", which is a legitimate answer -- it is not the same as
 * proposing the anchor back unchanged, and a caller must not treat it as one.
 *
 * Options that collapse onto the same (weight, reps) pair are de-duplicated:
 * three labels over two distinct sets would be a menu that lies about how much
 * choice it offers.
 */
export const buildSetProposals = (opts: {
  anchor: ProposalAnchor;
  source: ProposalSource;
  nowMs: number;
  /** Display unit of the row, so the load steps land on rungs the athlete can
   *  actually build in the unit they read the machine in. */
  unit?: "kg" | "lb";
  loadable?: LoadableContext;
}): SetProposal[] => {
  const { anchor, source, nowMs } = opts;
  const unit = opts.unit ?? "kg";
  const loadable = opts.loadable ?? {};

  if (!Number.isFinite(anchor.weightKg) || anchor.weightKg <= 0) return [];
  if (!Number.isInteger(anchor.reps) || !isE1rmRepCount(anchor.reps)) return [];

  const ageDays = daysBetween(anchor.atMs, nowMs);
  const base = epley(anchor.weightKg, anchor.reps);

  const oneUp = nextLoadableUp(anchor.weightKg, unit, loadable);
  const twoUp = nextLoadableUp(anchor.weightKg, unit, loadable, TRADE_RUNGS);

  // The trade drops reps as far as it can while still asking for more than
  // the anchor. ⚠️ The floor is the ANCHOR, not the load option: at a 2.5 kg
  // rung on a 100 kg lift one rung is worth ~2.5% and one rep is worth ~2.5%,
  // so "heavier AND above the load option" is a condition almost nothing
  // satisfies — a trade held to it would silently collapse back onto the same
  // reps as the load option, which is the duplicate this axis exists to avoid.
  // The trade is a different SHAPE of progress, not a better one.
  let tradeReps = anchor.reps;
  while (tradeReps > 1 && epley(twoUp, tradeReps - 1) > base) {
    tradeReps -= 1;
  }

  const raw: Array<{ axis: ProposalAxis; weightKg: number; reps: number }> = [
    { axis: "reps", weightKg: anchor.weightKg, reps: anchor.reps + 1 },
    { axis: "load", weightKg: oneUp, reps: anchor.reps },
    { axis: "trade", weightKg: twoUp, reps: tradeReps },
  ];

  const seen = new Set<string>();
  const out: SetProposal[] = [];
  for (const r of raw) {
    if (!isE1rmRepCount(r.reps)) continue;
    const kgPerRep1 = epley(r.weightKg, r.reps);
    // A proposal that does not ask for more than the anchor is not a
    // proposal. Guards the trade when the rungs are coarse relative to the
    // rep, which is exactly the light-dumbbell case.
    if (kgPerRep1 <= base) continue;
    const key = `${r.weightKg}x${r.reps}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      axis: r.axis,
      weightKg: r.weightKg,
      reps: r.reps,
      kgPerRep1,
      source,
      ageDays,
    });
  }
  return out;
};
